import React, { useState } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import { close, create, trash, time, chevronForward } from 'ionicons/icons';
import { useNeed, useDeleteNeed, useAddNeedUpdate } from '@/hooks/useNeeds';
import type { Need, NeedStatus } from '@/types/need';
import {
  NEED_CATEGORY_LABELS, NEED_CATEGORY_COLORS,
  NEED_STATUS_LABELS, NEED_STATUS_COLORS,
  NEED_PRIORITY_LABELS, NEED_PRIORITY_COLORS,
} from '@/utils/labels';

interface NeedDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  needId: string | null;
  onEdit?: (need: Need) => void;
  onDeleted?: () => void;
}

const Badge: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <span style={{
    display: 'inline-flex', padding: '3px 10px', borderRadius: 6,
    fontSize: 11, fontWeight: 600, color, background: `${color}15`,
  }}>
    {text}
  </span>
);

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const NeedDetailModal: React.FC<NeedDetailModalProps> = ({ isOpen, onClose, needId, onEdit, onDeleted }) => {
  const [presentToast] = useIonToast();
  const { data: need, isLoading } = useNeed(needId ?? undefined);
  const deleteMut = useDeleteNeed();
  const addUpdateMut = useAddNeedUpdate();

  const [updateContent, setUpdateContent] = useState('');
  const [updateStatus, setUpdateStatus] = useState<NeedStatus | ''>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleAddUpdate = async () => {
    if (!updateContent.trim() || !needId) return;
    try {
      await addUpdateMut.mutateAsync({
        needId,
        data: {
          content: updateContent.trim(),
          ...(updateStatus ? { status: updateStatus } : {}),
        },
      });
      setUpdateContent('');
      setUpdateStatus('');
      void presentToast({ message: 'Actualizare adăugată.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la adăugare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  const handleDelete = async () => {
    if (!needId) return;
    try {
      await deleteMut.mutateAsync(needId);
      void presentToast({ message: 'Nevoia a fost ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
      onDeleted?.();
      onClose();
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  if (!isOpen || !needId) return null;

  return (
    <div className="logout-overlay" onClick={onClose}>
      <div
        className="logout-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 540, width: '90%', maxHeight: '85vh', overflow: 'auto' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>
            {isLoading ? 'Se încarcă...' : need?.title ?? 'Nevoie'}
          </h3>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {need && onEdit && (
              <button
                onClick={() => { onEdit(need); onClose(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <IonIcon icon={create} style={{ fontSize: 18, color: '#5e81f4' }} />
              </button>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            >
              <IonIcon icon={trash} style={{ fontSize: 18, color: '#dc2626' }} />
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
            </button>
          </div>
        </div>

        {need && (
          <>
            {/* Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              <Badge text={NEED_STATUS_LABELS[need.status]} color={NEED_STATUS_COLORS[need.status]} />
              <Badge text={NEED_CATEGORY_LABELS[need.category]} color={NEED_CATEGORY_COLORS[need.category]} />
              <Badge text={NEED_PRIORITY_LABELS[need.priority]} color={NEED_PRIORITY_COLORS[need.priority]} />
            </div>

            {/* Info rows */}
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8, marginBottom: 16 }}>
              {need.family && (
                <div><strong>Familie:</strong> {need.family.display_name ?? need.family.name}</div>
              )}
              {need.member && (
                <div><strong>Persoană:</strong> {need.member.first_name} {need.member.last_name}</div>
              )}
              {need.reporter && (
                <div><strong>Raportat de:</strong> {need.reporter.name}</div>
              )}
              <div><strong>Creat:</strong> {fmtDate(need.created_at)}</div>
              {need.due_date && (
                <div><strong>Termen:</strong> {fmtDate(need.due_date)}</div>
              )}
              {need.resolved_date && (
                <div><strong>Rezolvat:</strong> {fmtDate(need.resolved_date)} {need.resolver ? `de ${need.resolver.name}` : ''}</div>
              )}
            </div>

            {/* Description */}
            {need.description && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                {need.description}
              </div>
            )}

            {/* Timeline / Updates */}
            {need.updates && need.updates.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1c1d21', margin: '0 0 10px' }}>
                  Actualizări ({need.updates.length})
                </h4>
                {need.updates.map((u) => (
                  <div key={u.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <IonIcon icon={time} style={{ fontSize: 12, color: '#94a3b8' }} />
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {u.user?.name ?? 'Sistem'} · {fmtDate(u.created_at)}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#1c1d21' }}>{u.content}</p>
                    {u.old_status && u.new_status && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11 }}>
                        <Badge text={NEED_STATUS_LABELS[u.old_status]} color={NEED_STATUS_COLORS[u.old_status]} />
                        <IonIcon icon={chevronForward} style={{ fontSize: 10, color: '#94a3b8' }} />
                        <Badge text={NEED_STATUS_LABELS[u.new_status]} color={NEED_STATUS_COLORS[u.new_status]} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add Update form */}
            {need.status !== 'rezolvata' && need.status !== 'anulata' && (
              <div style={{ borderTop: '1px solid #ececf2', paddingTop: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1c1d21', margin: '0 0 10px' }}>
                  Adaugă Actualizare
                </h4>
                <textarea
                  className="form-input"
                  value={updateContent}
                  onChange={(e) => setUpdateContent(e.target.value)}
                  placeholder="Ce s-a întâmplat / ce se face..."
                  rows={2}
                  style={{ resize: 'vertical', marginBottom: 10 }}
                />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <select
                    className="form-input"
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value as NeedStatus | '')}
                    style={{ flex: 1 }}
                  >
                    <option value="">Fără schimbare status</option>
                    {(Object.keys(NEED_STATUS_LABELS) as NeedStatus[])
                      .filter((s) => s !== need.status)
                      .map((s) => (
                        <option key={s} value={s}>{NEED_STATUS_LABELS[s]}</option>
                      ))}
                  </select>
                  <button
                    className="form-btn form-btn--primary"
                    onClick={handleAddUpdate}
                    disabled={!updateContent.trim() || addUpdateMut.isPending}
                    style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                  >
                    {addUpdateMut.isPending ? '...' : 'Adaugă'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.95)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderRadius: 14, padding: 24,
          }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#1c1d21', marginBottom: 16, textAlign: 'center' }}>
              Ștergi această nevoie?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="form-btn form-btn--secondary" onClick={() => setShowDeleteConfirm(false)}>
                Anulează
              </button>
              <button
                className="form-btn"
                style={{ background: '#dc2626', color: '#fff', border: 'none' }}
                onClick={handleDelete}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? '...' : 'Șterge'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedDetailModal;
